import { MutableRefObject, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePostCmsEmailIssuers } from "api/cms";
import { Button, FormSelect, Input } from "components";
import { Textarea } from "components/Textarea/Textarea";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { InfoIcon } from "lucide-react";
import { useGetContractIdData } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { Controller, useForm } from "react-hook-form";
import { contactIssuersSchema } from "./ContactSchemas";
import { useRelevantIssuers } from "./hooks/useRelevantIssuers";
import { IssuerSelect } from "./IssuerSelect";
import { Radio } from "./Select";
import { HOLDINGS_SETTINGS, Option, VALID_ASSET_TYPES } from "./types";

interface EmailIssuersModalProps {
  modalInitialFocusRef: MutableRefObject<null>;
  onClose: () => void;
}

export interface issuerFormData {
  /** The FA contact the portal is currently acting for. Shown read-only and
   * maps to `fromContact` on the CMS `EmailRequest`. */
  fromContact: string;
  holdingsSetting: Option<HOLDINGS_SETTINGS>;
  assetType: Option<VALID_ASSET_TYPES>;
  issuer: string;
  text: string;
  subject: string;
  externalIds: string[];
  senderName: string;
  senderEmail: string;
}

const LineBreakText = ({ text }: { text: string }) => {
  return (
    <div className="flex items-center mt-6 mb-1">
      <div className=" w-full bg-primary-20 h-[1px]"></div>
      <span className="mx-1 text-sm text-primary-40 whitespace-nowrap">
        {text}
      </span>
      <div className=" w-full bg-primary-20 h-[1px]"></div>
    </div>
  );
};

/**
 * The single switch that takes "Kontakta fondbolag" live: it enables the
 * submit button and removes the preview banner. Everything behind it is
 * already wired — the form submits, `usePostCmsEmailIssuers` posts to the CMS
 * route, the modal closes on success.
 *
 * Driven by `REACT_APP_CONTACT_ISSUERS_SEND_ENABLED` so it can be turned on
 * per environment without a code change. Anything other than the exact string
 * "true" — including the variable being absent — leaves sending off, so an
 * environment that never heard of this flag cannot accidentally start
 * emailing fund companies.
 *
 * Note CRA inlines this at build time: changing the env file needs a dev
 * server restart, not just a reload.
 */
const SENDING_ENABLED =
  process.env.REACT_APP_CONTACT_ISSUERS_SEND_ENABLED === "true";

const MailIssuersModalContent = ({ onClose }: EmailIssuersModalProps) => {
  const { t } = useModifiedTranslation();
  const { userProfile } = useKeycloak();
  const { sendEmail, isLoading: isSending } = usePostCmsEmailIssuers();
  const { selectedContact } = useGetContractIdData();

  const holdingsOptions: Option<HOLDINGS_SETTINGS>[] = [
    {
      id: HOLDINGS_SETTINGS.PORTFOLIO,
      label: t("contactModal.fields.holdings"),
    },
    {
      id: HOLDINGS_SETTINGS.ALL,
      label: t("contactModal.fields.allFunds"),
    },
  ];

  const assetTypeOptions: Option<VALID_ASSET_TYPES>[] = [
    {
      id: VALID_ASSET_TYPES.ALL,
      label: t("assetTypes.all"),
    },
    {
      id: VALID_ASSET_TYPES.SWEDEN_STOCKS,
      label: t("assetTypes.swedishStocks"),
    },
    {
      id: VALID_ASSET_TYPES.GLOBAL_STOCKS,
      label: t("assetTypes.globalStocks"),
    },
    {
      id: VALID_ASSET_TYPES.ALTERNATIVE,
      label: t("assetTypes.alternativeInvestments"),
    },
    {
      id: VALID_ASSET_TYPES.NOMINAL,
      label: t("assetTypes.nominalInterestRate"),
    },
    {
      id: VALID_ASSET_TYPES.REALA,
      label: t("assetTypes.realInterestRates"),
    },
  ];

  const resolver = zodResolver(contactIssuersSchema);

  const { watch, control, register, handleSubmit } = useForm<issuerFormData>({
    resolver: resolver,
    mode: "all",
    defaultValues: {
      fromContact: selectedContact.userName,
      assetType: assetTypeOptions[0],
      text: "",
      subject: "",
      externalIds: [],
      senderName: `${userProfile?.firstName} ${userProfile?.lastName}`,
      senderEmail: userProfile?.email,
    },
  });

  const holdingsSetting = watch("holdingsSetting")?.id;
  const assetTypeSetting = watch("assetType")?.id;
  const issuerOptions = useRelevantIssuers({
    holdingsSetting,
    assetTypeSetting,
  });

  const onSubmit = useCallback(
    async (formData: issuerFormData) => {
      // `sendEmail` is `mutateAsync`, so a failed send throws here and the
      // modal stays open with the user's message intact. The error toast is
      // raised by the hook's onError.
      try {
        await sendEmail(formData);
      } catch {
        return;
      }
      onClose();
    },
    [sendEmail, onClose]
  );

  return (
    <div>
      {/* While SENDING_ENABLED is false this stays a preview: submitting is a
          no-op, so a stray Enter keypress in a text field cannot reach the
          API. */}
      <form
        onSubmit={
          SENDING_ENABLED ? handleSubmit(onSubmit) : (e) => e.preventDefault()
        }
      >
        <div className="flex flex-col gap-8 w-[600px]">
          <div className="flex flex-col gap-4">
            {!SENDING_ENABLED && (
              <div className="flex items-center px-4 py-3 w-full rounded-lg bg-secondary-100 text-white-100">
                <span>
                  <InfoIcon className="mr-4 w-10 h-10 shrink-0 text-white-100" />
                </span>
                <div>
                  <h3 className="mb-1 font-semibold text-headline-3 text-white-100">
                    {t("contactModal.comingSoon.header")}
                  </h3>
                  <p className="text-sm text-balance text-white-100">
                    {t("contactModal.comingSoon.explainer")}
                  </p>
                </div>
              </div>
            )}
            <LineBreakText text={t("contactModal.whoToContact")} />
            <Controller
              control={control}
              name="holdingsSetting"
              render={({ field: { onChange, value } }) => (
                <Radio
                  name="holdingsSetting"
                  label={t("contactModal.fields.whatToContact")}
                  options={holdingsOptions}
                  selected={value}
                  onSelect={onChange}
                />
              )}
            />
            <FormSelect
              text="sm"
              fontWeight="font-normal"
              name="assetType"
              control={control}
              label={t("contactModal.fields.assetType")}
              options={assetTypeOptions}
            />
            {holdingsSetting && (
              <Controller
                control={control}
                name="externalIds"
                render={({ field: { onChange, value } }) => (
                  <div>
                    <label className="text-sm font-normal">
                      {t("contactModal.fields.pickIssuers")}
                    </label>
                    <IssuerSelect
                      issuers={issuerOptions.data}
                      hasContactableIssuers={
                        issuerOptions.hasContactableIssuers
                      }
                      selected={value}
                      onSelect={onChange}
                    />
                  </div>
                )}
              />
            )}
            <LineBreakText text={t("contactModal.yourInfo")} />
            {/* Always disabled: the contact is decided by who the portal is
                acting for (incl. an impersonated representee), never typed. */}
            <Input
              label={t("contactModal.fields.fromContact")}
              disabled
              type="text"
              {...register("fromContact")}
            />
            <Input
              label={t("contactModal.fields.name")}
              required
              type="text"
              {...register("senderName")}
            />
            <Input
              label={t("contactModal.fields.email")}
              type="email"
              required
              {...register("senderEmail")}
            />
            <Input
              label={t("contactModal.fields.subject")}
              required
              type="text"
              {...register("subject")}
            />
            <Textarea
              label={t("contactModal.fields.text")}
              className="min-h-[140px]"
              required
              {...register("text")}
            />
          </div>
          {/* Always enabled once live (bar the in-flight state): field errors
              on click explain themselves, a dead button does not. */}
          <Button
            disabled={!SENDING_ENABLED}
            isLoading={isSending}
            type="submit"
            variant="Primary"
            size="md"
            rounded="lg"
          >
            <span>
              {SENDING_ENABLED
                ? t("contactModal.send")
                : t("contactModal.comingSoon.label")}
            </span>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MailIssuersModalContent;
